import React, { useState, useEffect, useRef } from 'react';
import { get, post, put } from '../services/apiController';
import { TABLE_DEFAULT_LIMIT, DEBOUNCE_DELAY } from '../util/constant';
import { Table, Modal, Button, Form, FloatingLabel, Row, Col } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import { EditIcon } from './icons';
import 'react-toastify/dist/ReactToastify.css';
import PaginationInPage from './Pagination';
import useDebounce from './useDebounce';
import { GrDocumentExcel } from 'react-icons/gr';
import Breadcrumb from '../components/Breadcrumb';
import { useSelector } from 'react-redux';

const Companies = () => {
  const formFields = { name: '', address: '' };
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [companyForm, setCompanyForm] = useState(formFields);
  const [showConfirmModel, setShowConfirmModel] = useState(false);
  const [companyList, setCompanyList] = useState([]);
  const [query, setQuery] = useState('');
  const clickToDownload = useRef(null);

  const permissions = useSelector((state) => state.permission.permissions);

  const handleShow = () => {
    setShowConfirmModel(true);
    setCompanyForm(formFields);
  };

  const handleClose = () => {
    setShowConfirmModel(false);
  };

  const handleEdit = (e, id) => {
    e.preventDefault();
    try {
      get('/companies/?id=' + id).then((company) => {
        if (company.data.rows.length) {
          const companyData = company.data.rows[0];
          setCompanyForm(companyData);
          setShowConfirmModel(true);
        } else {
          toast.error('No company found with this name');
        }
      });
    } catch (error) {
      toast.error(error?.response?.data?.data?.rows?.message || 'Something went wrong');
    }
  };

  const handleChange = (event) => {
    event.preventDefault();
    let name = event.target.name;
    let value = event.target.value;
    setCompanyForm({ ...companyForm, [name]: value });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if ('id' in companyForm) {
      try {
        await put(companyForm, '/companies/' + companyForm.id);
        toast.success('Company updated successfully');
        setShowConfirmModel(false);
        getCompanies();
      } catch (err) {
        toast.error(err.message, { autoClose: 3000 });
      }
    } else {
      try {
        await post(companyForm, '/companies');
        toast.success('Company added successfully');
        setShowConfirmModel(false);
        getCompanies();
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.response?.data?.data?.message || err.message, { autoClose: 2000 });
      }
    }
  };

  useDebounce(
    () => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        getCompanies();
      }
    },
    [query],
    DEBOUNCE_DELAY
  );

  function getCompanies() {
    try {
      get(`/companies/?page=${currentPage}&limit=${TABLE_DEFAULT_LIMIT}&query=${query}`).then((response) => {
        setCompanyList(response.data.rows);
        setTotalRecords(response.data.count);
      });
    } catch (err) {
      toast.error(err.response.data.data.message);
    }
  }

  function excelReport() {
    try {
      get(`/companies/?conditionType=download`).then((response) => {
        clickToDownload.current.href = `${process.env.REACT_APP_BASE_URL}/file/download?filePath=${response.data.filePath}`;
        setTimeout(() => {
          clickToDownload.current.click();
        }, 500);
      });
    } catch (err) {
      toast.error(err.response.data.data.message);
    }
  }

  useEffect(() => {
    getCompanies();
  }, [currentPage]);

  return (
    <React.Fragment>
      <ToastContainer />
      <Breadcrumb
        navigation={[
          { link: '#', title: 'Settings' },
          { link: '#', title: 'Companies' },
        ]}
      />
      <div className="card">
        <div className="card-header white-color" style={{ padding: '1rem 1.7rem' }}>
          <h4 className="card-title mb-0" style={{ fontSize: '16px' }}>
            Companies | {totalRecords ?? 0}
          </h4>
          <div className="card-header-actions">
            <Form.Control
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              type="search"
              placeholder="Search by company name"
              style={{ width: '250px', marginRight: '20px' }}
            />
            <GrDocumentExcel title="Download companies" className="react-icons green" onClick={excelReport} />
            <a ref={clickToDownload} href="" target="_blank" className="hiddenItem">
              HiddenClick
            </a>
            {/* {permissions.includes('add_company') && (
              <button type="button" className="btn app-btn-default dark-color " onClick={handleShow}>
                Add Company
              </button>
            )} */}
          </div>
        </div>
        <div className="card-body px-0 vehicle-requests-table pt-0 pb-0">
          <div className="row">
            <div className="col">
              <Table responsive striped>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Company Name</th>
                    <th>Company Address</th>
                    {permissions.includes('edit_company') && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {companyList.map((item, index) => {
                    return (
                      <tr key={item?.id}>
                        <td>{(currentPage - 1) * TABLE_DEFAULT_LIMIT + (index + 1)}</td>
                        <td>{item?.name}</td>
                        <td>{item?.address}</td>
                        {permissions.includes('edit_company') && (
                          <td className="actions">
                            <span onClick={(e) => handleEdit(e, item?.id)} className="edit-icon">
                              <EditIcon style={{ color: 'teal' }} />
                            </span>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              <PaginationInPage
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalRecords={totalRecords}
                TABLE_DEFAULT_LIMIT={TABLE_DEFAULT_LIMIT}
              />
            </div>
          </div>
        </div>
      </div>
      <Modal size="lg" show={showConfirmModel} backdrop="static" keyboard={false} className="request-modal">
        <Modal.Header>
          <Modal.Title>{'id' in companyForm ? 'Edit Company' : 'Add Company'}</Modal.Title>
          <button type="button" className="btn-close" aria-label="Close" onClick={handleClose}></button>
        </Modal.Header>
        <Modal.Body>
          <Form autoComplete="off">
            <Row>
              <Col xs={6}>
                <FloatingLabel controlId="floatingInput" label="Company Name" className="mb-3">
                  <Form.Control
                    className="input"
                    name="name"
                    value={companyForm.name}
                    onChange={handleChange}
                    type="text"
                    placeholder="Company Name"
                    required
                  />
                </FloatingLabel>
              </Col>
              <Col xs={6}>
                <FloatingLabel controlId="floatingInput" label="Company Address" className="mb-3">
                  <Form.Control
                    className="input"
                    name="address"
                    value={companyForm.address}
                    onChange={handleChange}
                    type="text"
                    placeholder="Company Address"
                    required
                  />
                </FloatingLabel>
              </Col>
            </Row>
            <div className="container text-center">
              <Button type="submit" onClick={handleSubmit} className="add-request-btn mt-4 mb-3">
                {'id' in companyForm ? 'Update' : 'Add'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};

export default Companies;
